import { CloudCheck } from "lucide-react";

import { DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { InitialsAvatar } from "@/components/ui/initials-avatar";
import { useAuthSession } from "@/lib/session-context";
import { cn } from "@/lib/utils";

import { PREFERENCES_SECTIONS, type PreferencesSectionId } from "./preferences-sections";

/**
 * The Preferences dialog's left rail: the dialog's title, one row per section
 * (icon, name, what it holds), and whose preferences these are. Below `sm`
 * the rows become a scrolling strip across the top.
 */
export function PreferencesNav({
  active,
  onSelect,
}: {
  active: PreferencesSectionId;
  onSelect: (id: PreferencesSectionId) => void;
}) {
  const session = useAuthSession();

  return (
    <nav
      aria-label="Preferences sections"
      className="flex shrink-0 flex-col border-b border-border bg-card sm:w-64 sm:border-b-0 sm:border-r"
    >
      <div className="flex flex-col gap-1 px-5 pb-3 pt-5">
        <DialogTitle className="text-lg">Preferences</DialogTitle>
        <DialogDescription className="flex items-center gap-1.5 text-xs">
          <CloudCheck size={13} strokeWidth={1.75} className="shrink-0 text-primary" />
          Synced to your account
        </DialogDescription>
      </div>

      <ul className="scrollbar-thin flex gap-1 overflow-x-auto px-3 pb-3 sm:flex-1 sm:flex-col sm:overflow-x-visible">
        {PREFERENCES_SECTIONS.map((s) => {
          const Icon = s.icon;
          const selected = s.id === active;
          return (
            <li key={s.id} className="shrink-0">
              <button
                type="button"
                onClick={() => onSelect(s.id)}
                aria-current={selected ? "page" : undefined}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-mid",
                  selected ? "bg-nav-active-bg" : "hover:bg-row-hover",
                )}
              >
                <span
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors",
                    selected ? "bg-primary-dim text-primary" : "bg-surface-elevated text-text-dim",
                  )}
                >
                  <Icon size={16} strokeWidth={1.75} />
                </span>
                <span className="flex min-w-0 flex-col leading-tight">
                  <span
                    className={cn(
                      "text-sm font-medium",
                      selected ? "text-primary" : "text-foreground",
                    )}
                  >
                    {s.label}
                  </span>
                  <span className="hidden truncate text-xs text-muted-foreground sm:block">
                    {s.hint}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="hidden items-center gap-2.5 border-t border-border px-5 py-4 sm:flex">
        <InitialsAvatar name={session.display_name} initials={session.initials} size={28} />
        <div className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-sm font-medium text-foreground">{session.display_name}</span>
          <span className="truncate text-xs text-muted-foreground">{session.role_label}</span>
        </div>
      </div>
    </nav>
  );
}
